/* This file is a copy of the Apollo Client Provider defined in the Settings app.    */
/* It enables the fetching of OLDER API keys through a GraphQL call. So, it is       */
/* a temporary logic before fetching GKD API keys through the Partner Credentials    */
/* service.                                                                          */
import { ApolloClient, ApolloProvider, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';
import { createUploadLink } from 'apollo-upload-client';
import { selectors as configSelectors } from 'mage-config';
import { selectors as coreSelectors } from 'mage-core';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMid } from '../hooks/useMid';
function createClient({ getToken, getSettingsUrl }) {
    const authLink = setContext((_, { headers }) => {
        const token = getToken();
        return {
            headers: Object.assign(Object.assign({}, headers), { authorization: token ? `Bearer ${token}` : '' })
        };
    });
    const httpLink = createUploadLink({
        uri: () => `${getSettingsUrl()}/graphql`
    });
    return new ApolloClient({
        link: onError(handleError).concat(authLink).concat(httpLink),
        cache: new InMemoryCache({
            typePolicies: {
                MerchantCredential: {
                    fields: {
                        properties: {
                            merge: false
                        }
                    }
                }
            }
        })
    });
}
function SettingsApolloProvider({ children }) {
    // @ts-ignore ANY
    const token = useSelector(coreSelectors.getAccessToken);
    const getMerchantRegion = useSelector(coreSelectors.getMerchantRegion);
    const baseUrls = useSelector(configSelectors.getMerchantPortalBaseUrls);
    // @ts-ignore ANY
    const getBackendUrlForRegion = useSelector(coreSelectors.generateGetBackendUrlForRegion);
    const { selectedMid: mid } = useMid();
    if (!mid)
        return null;
    const region = getMerchantRegion(mid);
    // @ts-ignore ANY
    const host = baseUrls[region === null || region === void 0 ? void 0 : region.toLowerCase()] || '';
    const settingsPath = getBackendUrlForRegion({ region, backendName: 'merchant-portal-settings' });
    const tokenRef = useRef(token);
    const urlRef = useRef();
    urlRef.current = `${host}${settingsPath}`;
    const [shouldThrowError, setShouldThrowError] = useState(false);
    const [client] = useState(() => createClient({
        getToken: () => tokenRef.current,
        getSettingsUrl: () => urlRef.current || ''
    }));
    useEffect(() => {
        tokenRef.current = token;
    }, [token]);
    useEffect(() => {
        if (!region) {
            const timer = setTimeout(() => setShouldThrowError(true), 60000);
            return () => clearTimeout(timer);
        }
    }, [region]);
    if (shouldThrowError) {
        throw new Error('No region has been provided after some time.');
    }
    return (React.createElement(ApolloProvider, { client: client }, children));
}
function handleError(response) {
    const { graphQLErrors, networkError, operation } = response;
    const operationName = operation.operationName;
    const variables = operation.variables;
    graphQLErrors === null || graphQLErrors === void 0 ? void 0 : graphQLErrors.forEach(error => {
        var _a, _b;
        const errorCode = (_a = error === null || error === void 0 ? void 0 : error.extensions) === null || _a === void 0 ? void 0 : _a.code;
        const errorId = (_b = error === null || error === void 0 ? void 0 : error.extensions) === null || _b === void 0 ? void 0 : _b.errorId;
        if (errorCode === 'INTERNAL_SERVER_ERROR') {
            Sentry.captureMessage(`${operationName} failed with graphql error`, {
                level: 'error',
                fingerprint: ['graphql error', operationName],
                extra: {
                    errorId,
                    variables
                }
            });
        }
    });
    if (networkError) {
        Sentry.captureMessage(`${operationName} failed with network error: ${networkError.name}`, {
            level: 'error',
            fingerprint: ['network error', operationName, networkError.name],
            extra: {
                variables,
                stack: networkError.stack,
                message: networkError.message
            }
        });
    }
}
export default SettingsApolloProvider;
